/**
 * HonorRateCard.jsx — Iter 179.19 (Feb 2026)
 * ==========================================
 *
 * A drop-in card that shows a rep's committed-callback honor rate over
 * a configurable time window. Backend: GET /api/admin/rep-activity/{user_id}/honor-rate
 *
 * Design:
 *   - Big-number honor-rate percentage with color-coded band
 *     (>=80% green, 60-79% amber, <60% red, null gray "—").
 *   - Below: commits / kept ratio in small text.
 *   - Below that: a mini weekly sparkline showing recent trend.
 *
 * Guardrails:
 *   - Returns null (renders nothing) on 404 endpoint_disabled OR any
 *     network failure — never breaks the surrounding page.
 *   - Admin/manager only (backend enforces 403 for reps).
 *   - Respects the page's date-filter state via `since` / `until` props.
 *
 * Usage:
 *   <HonorRateCard userId={selectedRepId} since={customFrom} until={customTo} />
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function bandColor(pct) {
  if (pct === null || pct === undefined) return "#9CA3AF"; // gray
  if (pct >= 80) return "#10B981";
  if (pct >= 60) return "#F59E0B";
  return "#DC2626";
}

export default function HonorRateCard({ userId, since, until }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | hidden

  useEffect(() => {
    if (!userId) { setState("hidden"); return; }
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
    if (!token) { setState("hidden"); return; }
    let cancelled = false;
    setState("loading");
    (async () => {
      try {
        const params = {};
        if (since) params.since = since;
        if (until) params.until = until;
        const r = await axios.get(
          `${API}/api/admin/rep-activity/${encodeURIComponent(userId)}/honor-rate`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params,
          }
        );
        if (!cancelled) { setData(r.data); setState("ok"); }
      } catch (err) {
        // 404 endpoint_disabled OR 403 non-manager OR network — hide silently.
        if (!cancelled) setState("hidden");
      }
    })();
    return () => { cancelled = true; };
  }, [userId, since, until]);

  if (state !== "ok" || !data) return null;
  const pct = data.honor_rate_pct;
  const color = bandColor(pct);

  return (
    <div
      data-testid="honor-rate-card"
      style={{
        border: "1px solid #E5E7EB", borderRadius: 12, padding: "16px 20px",
        background: "#FFFFFF", minWidth: 240,
        display: "flex", flexDirection: "column", gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span style={{ fontSize: 12, textTransform: "uppercase",
                       letterSpacing: 0.6, color: "#6B7280", fontWeight: 600 }}>
          Committed-callback honor rate
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span data-testid="honor-rate-value"
              style={{ fontSize: 34, fontWeight: 700, color, lineHeight: 1 }}>
          {pct === null ? "—" : `${pct.toFixed(1)}%`}
        </span>
        <span style={{ fontSize: 12, color: "#6B7280" }}>
          {data.kept}/{data.commits} kept
        </span>
      </div>
      {data.breakdown_by_week && data.breakdown_by_week.length > 0 && (
        <div style={{ display: "flex", gap: 2, height: 20, alignItems: "flex-end" }}
             data-testid="honor-rate-sparkline">
          {data.breakdown_by_week.slice(-8).map((w, i) => {
            const h = w.pct === null ? 2 : Math.max(2, Math.round(w.pct * 0.2));
            return (
              <div key={i}
                   title={`Week of ${w.week_start}: ${w.kept}/${w.commits}`}
                   style={{ width: 6, height: h, background: bandColor(w.pct),
                            borderRadius: 1, opacity: 0.85 }} />
            );
          })}
        </div>
      )}
      <div style={{ fontSize: 10, color: "#9CA3AF" }}>
        Window ±{data.window_minutes} min · last {Math.round(
          (new Date(data.until) - new Date(data.since)) / 86400000
        )}d
      </div>
    </div>
  );
}
